import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import { PaginatedResult, UserSummary, UserDetail, UserRoleScope, UserScopeJson, AssignUserScopeRequest } from '#models';
import { IdpSource } from '#enums';

export interface UserFilters {
    roleId?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    idpSource?: IdpSource;
    isActive?: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserManagementService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/users`;

    getUsers(filters: UserFilters = {}): Observable<PaginatedResult<UserSummary>> {
        const params: Record<string, string> = {};
        if (filters.roleId) params['roleId'] = filters.roleId;
        if (filters.search) params['search'] = filters.search;
        if (filters.page != null) params['page'] = String(filters.page);
        if (filters.pageSize != null) params['pageSize'] = String(filters.pageSize);
        if (filters.sortBy) params['sortBy'] = filters.sortBy;
        if (filters.sortDir) params['sortDir'] = filters.sortDir;
        if (filters.idpSource != null) params['idpSource'] = String(filters.idpSource);
        if (filters.isActive != null) params['isActive'] = String(filters.isActive);
        return this._http.get<PaginatedResult<UserSummary>>(this._base, { params });
    }

    getUserById(userId: string): Observable<UserDetail> {
        return this._http.get<UserDetail>(`${this._base}/${userId}`);
    }

    updateRole(userId: string, newRoleId: string): Observable<void> {
        return this._http.put<void>(`${this._base}/${userId}/role`, { userId, newRoleId });
    }

    getScopes(userId: string): Observable<UserRoleScope[]> {
        return this._http.get<UserRoleScope[]>(`${this._base}/${userId}/scopes`);
    }

    assignScope(userId: string, dto: Omit<AssignUserScopeRequest, 'userId'>): Observable<UserRoleScope> {
        return this._http.post<UserRoleScope>(`${this._base}/${userId}/scopes`, { ...dto, userId });
    }

    updateScope(userId: string, scopeId: number, scopes: UserScopeJson | null): Observable<UserRoleScope> {
        return this._http.patch<UserRoleScope>(`${this._base}/${userId}/scopes/${scopeId}`, { scopes });
    }

    removeScope(userId: string, scopeId: number): Observable<void> {
        return this._http.delete<void>(`${this._base}/${userId}/scopes/${scopeId}`);
    }

    toggleActive(userId: string, isActive: boolean): Observable<void> {
        // X-Handle-Forbidden tells the interceptor not to redirect — caller shows the reason instead
        return this._http.patch<void>(`${this._base}/${userId}/active`, { isActive }, {
            headers: { 'X-Handle-Forbidden': '1' },
        });
    }
}
